import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, Megaphone, MapPin, Search, Flag, MessageSquare, ArrowLeft, Send } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export const CATS = { lavoro_infermieri: "Lavoro infermieri", lavoro_medici: "Lavoro medici", lavoro_oss: "Lavoro OSS", personale_rsa: "Personale RSA e cliniche", collaborazione: "Collaborazioni", servizi: "Servizi sanitari", eventi: "Eventi", ufficiale: "Comunicazioni ufficiali", community: "Community" };

function Badge({ p }) {
  if (p.official) return <span className="flex items-center gap-1 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold text-white"><Megaphone className="h-3 w-3" />Ufficiale</span>;
  if (p.verified) return <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800"><BadgeCheck className="h-3 w-3" />Verificato</span>;
  return <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">Non verificato</span>;
}

function PostChat({ p, onClose }) {
  const { user } = useAuth();
  const [msg, setMsg] = useState("");
  const [sent, setSent] = useState([]);
  const post = async (url, body, ok) => {
    try {
      await api.post(url, body);
      toast.success(ok);
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };
  const send = async () => {
    if (await post(`/cafe/board/${p.id}/apply`, { message: msg }, "Messaggio inviato all'inserzionista")) {
      setSent((s) => [...s, { id: `${Date.now()}-${s.length}`, text: msg }]);
      setMsg("");
    }
  };
  return (
    <div className="absolute inset-0 z-30 flex flex-col rounded-3xl bg-white p-3" data-testid={`board-chat-${p.id}`}>
      <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
        <button data-testid="board-chat-close" onClick={onClose} aria-label="Torna alla bacheca" className="grid h-8 w-8 place-items-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200"><ArrowLeft className="h-4 w-4" /></button>
        <div className="min-w-0 flex-1"><p className="truncate text-sm font-bold text-slate-900">{p.title}</p><p className="truncate text-[11px] text-slate-500">{p.company || p.author} · {CATS[p.category]}</p></div>
        <Badge p={p} />
      </div>
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto py-3">
        <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-emerald-50 p-3 text-sm text-slate-800" data-testid="board-chat-post">
          <p className="mb-1 text-[11px] font-bold text-emerald-800">{p.author}</p>
          <p className="whitespace-pre-wrap">{p.description}</p>
          {p.contact && <p className="mt-2 text-xs text-slate-600">Contatto: {p.contact}</p>}
          <p className="mt-2 text-[10px] text-slate-500">{fmtDate(p.published_at)}{p.location && ` · ${p.location}`}{p.expires_at && ` · scade ${fmtDate(p.expires_at)}`}</p>
        </div>
        {sent.map((m) => <div key={m.id} className="ml-auto max-w-[85%] rounded-2xl rounded-tr-sm bg-emerald-600 p-3 text-sm text-white">{m.text}</div>)}
      </div>
      {!user && <p className="border-t border-slate-100 pt-2 text-xs text-amber-700">Accedi per rispondere all'inserzionista.</p>}
      {user && sent.length > 0 && <p className="border-t border-slate-100 pt-2 text-xs text-emerald-700" data-testid="board-chat-sent-note">Messaggio inviato: l'inserzionista ti risponderà tramite le notifiche.</p>}
      {user && sent.length === 0 && (
        <div className="flex gap-2 border-t border-slate-100 pt-2">
          <Input data-testid={`board-apply-input-${p.id}`} value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && msg.trim().length >= 10) send(); }} placeholder="Scrivi all'inserzionista (min. 10 caratteri)" />
          <Button size="icon" data-testid={`board-apply-${p.id}`} disabled={msg.trim().length < 10} onClick={send} className="bg-emerald-600 hover:bg-emerald-700" aria-label="Invia"><Send className="h-4 w-4" /></Button>
        </div>
      )}
      {user && <button data-testid={`board-report-${p.id}`} onClick={() => post(`/cafe/board/${p.id}/report`, { reason: "segnalato dalla bacheca" }, "Annuncio segnalato")} className="mt-1 flex items-center gap-1 self-start text-[11px] text-slate-400 hover:text-rose-600"><Flag className="h-3 w-3" />Segnala annuncio</button>}
    </div>
  );
}

function PostCard({ p, onOpen }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`board-post-${p.id}`}>
      <div className="flex items-start justify-between gap-2"><p className="text-sm font-bold text-slate-900">{p.title}</p><Badge p={p} /></div>
      <p className="text-[11px] text-slate-500">{CATS[p.category]} · {p.company || p.author} · {fmtDate(p.published_at)}{p.location && <> · <MapPin className="inline h-3 w-3" /> {p.location}</>}</p>
      <button data-testid={`board-details-${p.id}`} onClick={() => onOpen(p)} className="mt-1 flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline"><MessageSquare className="h-3.5 w-3.5" />Apri in chat</button>
    </div>
  );
}

function NewPost({ onDone }) {
  const [f, setF] = useState({ title: "", category: "community", description: "", location: "", company: "", contact: "", expires_at: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/cafe/board", { ...f, expires_at: f.expires_at || null });
      toast.success(data.status === "pubblicato" ? "Annuncio pubblicato" : "Annuncio inviato: sarà visibile dopo la moderazione");
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={submit} className="space-y-2 rounded-xl bg-emerald-50 p-3" data-testid="board-new-form">
      <Input data-testid="board-new-title" required placeholder="Titolo" value={f.title} onChange={set("title")} />
      <select data-testid="board-new-category" value={f.category} onChange={set("category")} className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">{Object.entries(CATS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
      <Textarea data-testid="board-new-description" required placeholder="Descrizione (mansioni, requisiti, orari)" value={f.description} onChange={set("description")} />
      <div className="grid grid-cols-2 gap-2">
        <Input data-testid="board-new-location" placeholder="Località" value={f.location} onChange={set("location")} />
        <Input data-testid="board-new-company" placeholder="Azienda / struttura" value={f.company} onChange={set("company")} />
        <Input data-testid="board-new-contact" placeholder="Contatto pubblico (facoltativo)" value={f.contact} onChange={set("contact")} />
        <Input data-testid="board-new-expires" type="date" value={f.expires_at} onChange={set("expires_at")} />
      </div>
      <Button data-testid="board-new-submit" className="w-full bg-emerald-600 hover:bg-emerald-700">Invia per la pubblicazione</Button>
    </form>
  );
}

export function Board() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [dq, setDq] = useState("");
  const [chat, setChat] = useState(null);
  const reqId = useRef(0);
  useEffect(() => {
    const t = setTimeout(() => setDq(q), 300);
    return () => clearTimeout(t);
  }, [q]);
  const load = useCallback(() => {
    const id = (reqId.current += 1);
    return api.get("/cafe/board", { params: { category: cat || undefined, q: dq || undefined } }).then(({ data }) => {
      if (id === reqId.current) setItems(data);
    });
  }, [cat, dq]);
  useEffect(() => {
    load();
  }, [load]);
  return (
    <div className="space-y-3" data-testid="cafe-board">
      <div className="flex gap-2">
        <div className="relative flex-1"><Search className="absolute left-2.5 top-3 h-4 w-4 text-slate-400" /><Input data-testid="board-search" className="pl-8" placeholder="Cerca annunci" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <select data-testid="board-filter" value={cat} onChange={(e) => setCat(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-2 text-sm"><option value="">Tutte</option>{Object.entries(CATS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
      </div>
      {user && <Button size="sm" variant="outline" data-testid="board-new-btn" onClick={() => setAdding(!adding)}>{adding ? "Annulla" : "Pubblica un annuncio"}</Button>}
      {adding && <NewPost onDone={() => { setAdding(false); load(); }} />}
      {!items.length && <p className="text-sm text-slate-500" data-testid="board-empty">Nessun annuncio pubblicato.</p>}
      {items.map((p) => <PostCard key={p.id} p={p} onOpen={setChat} />)}
      {chat && <PostChat p={chat} onClose={() => setChat(null)} />}
    </div>
  );
}
